import { useUsers } from '../../../hooks/useUsers';
import { Select, type SelectOption } from '../../ui/Select';
import { useI18n } from '../../../contexts/I18nContext';

// Sentinel selection for the business-wide ("All Users") overview (CLE-58).
export const ALL_USERS = 'all-users';

interface Props {
  selectedUserId: string;
  onChange: (userId: string) => void;
}

/**
 * The picker's options, exposed so the mobile filter sheet on the daily and
 * weekly pages lists exactly the same people with the same labels.
 */
export function useUserOptions(): { options: SelectOption[]; loading: boolean } {
  const { t } = useI18n();
  const { data: users, isLoading } = useUsers();
  return {
    loading: isLoading,
    options: [
      { value: ALL_USERS, label: t('daily.allUsersOverview') },
      ...(users ?? []).map((user) => ({
        value: user.id,
        label: `${user.name} (${user.role})`,
      })),
    ],
  };
}

/**
 * Team-member dropdown for the daily summary (TASK-FE-DAILY-007). The first
 * option is "All Users" (business-wide overview, the default per CLE-58);
 * selecting a member switches to that individual's summary + calendar.
 * Selection is lifted to the page which syncs the URL.
 */
export function UserSelector({ selectedUserId, onChange }: Props) {
  const { t } = useI18n();
  const { options, loading } = useUserOptions();

  // No wrapper and no `hideLabel`: Select already renders the label above the
  // trigger with `ui-field-label`, which is how every other filter in the app
  // is labelled. The old inline <span> put it to the left instead, and on a
  // phone that left the control too narrow to read.
  return (
    <Select
      label={t('common.user')}
      value={selectedUserId}
      onChange={onChange}
      disabled={loading}
      loading={loading}
      options={options}
      className="w-full sm:w-64"
    />
  );
}
